import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePreservedCallback } from '@toss/react';
import { useRouter } from 'next/router';

import { trackEvent } from '@zep/logger/tracking';

import { type QuizBuildQuestionStepPlace } from '@/shared/quiz/events.quiz-build';

import { focusField } from '../components/focus/focusUtils';
import { useBuildInfoContext } from '../context';
import { BuildSchema, useBuildFormContext } from '../form/schema';
import { getBuildType } from '../util/getBuildType';

import { type BuildStep } from './BuildHeader';
import { getQuestionStepSubmitProps } from './getQuestionStepSubmitProps';
import { useRemoveEmptyAnswerInputs } from './useRemoveEmptyAnswerInputs';
import { useValidateQuizList } from './useValidateQuizList';

/** BuildPageBody 가 step 전환 시 scrollTo(top) 하므로, 그 이후에 포커스되도록 미룬다. */
const STEP_CHANGE_FOCUS_DELAY_MS = 100;

export const useBuildStep = () => {
  const [step, setStep] = useBuildStepWithInitialTarget();

  const [isSubmitButtonShown, setIsSubmitButtonShown] = useState(
    () => step === 'settings',
  );

  const { getValues } = useBuildFormContext();

  const validateQuizList = useValidateQuizList();

  const removeEmptyAnswerInputs = useRemoveEmptyAnswerInputs();

  const { action } = useBuildInfoContext();

  const questionStepAttemptCountRef = useRef(0);

  const handleStepChange = async (
    newStep: BuildStep,
    place?: QuizBuildQuestionStepPlace,
  ) => {
    if (newStep === 'settings') {
      const cleanupResult = removeEmptyAnswerInputs();

      const validation = await validateQuizList();

      if (place) {
        trackEvent('quiz_build_question_step_submit', {
          build_type: getBuildType(action),
          place,
          attempt_index: ++questionStepAttemptCountRef.current,
          ...getQuestionStepSubmitProps({
            quizList: getValues('quizList'),
            cleanupResult,
            validation,
          }),
        });
      }

      if (!validation.isValid) {
        return;
      }

      //모두 통과하면 일단 제출 버튼을 보여줌
      setIsSubmitButtonShown(true);
      setStep(newStep);
    } else {
      // 설정 -> 퀴즈
      const formData = getValues();
      const { success, error } = BuildSchema.safeParse(formData);

      // 퀴즈 리스트 이외의 필드에 대해 유효성 검사를 통과하지 못하면 제출 버튼을 숨김
      if (
        !success &&
        error?.errors.some(error => !error.path.includes('quizList'))
      ) {
        setIsSubmitButtonShown(false);
      }
      setStep(newStep);
    }
  };

  /**
   * 문제 탭의 필드로 이동한다. 설정 탭에선 문제 탭이 display:none 이라 바로 포커스할 수 없어
   * 문제 탭으로 먼저 전환한다. 모달 콜백·effect 에 넘겨도 되도록 참조를 고정한다.
   */
  const focusQuizField = usePreservedCallback((fieldId: string) => {
    if (step !== 'quiz') {
      void handleStepChange('quiz');
    }
    focusField(fieldId, {
      delay: STEP_CHANGE_FOCUS_DELAY_MS,
      scroll: true,
      focus: true,
    });
  });

  return {
    step,
    handleStepChange,
    isSubmitButtonShown,
    focusQuizField,
  };
};

/**
 * 수정 페이지 진입 시, 수정 모드인지 확인하고 router query를 읽어 초기 진입점을 결정합니다.
 * 수정 모드가 아닌 경우, 퀴즈 탭으로 진입합니다.
 */
export const useBuildStepWithInitialTarget = () => {
  const router = useRouter();

  const isUpdate = useMemo(
    () => router.pathname.includes('/update'),
    [router.pathname],
  );

  const initialTab =
    isUpdate && router.query.initialStep === 'settings' ? 'settings' : 'quiz';

  const initialTargetField = router.query.targetField;
  const [step, setStep] = useState<BuildStep>(initialTab as BuildStep);

  useLayoutEffect(() => {
    if (!isUpdate) return;

    if (initialTargetField) {
      focusField(initialTargetField as string, {
        delay: STEP_CHANGE_FOCUS_DELAY_MS,
        scroll: true,
        focus: true,
      });
    }
  }, [initialTargetField, isUpdate]);

  return [step, setStep] as const;
};
